/**
 * A property, on the shared record page.
 *
 * The mirror of `contact-record.tsx`, and the proof that the shell is a shell:
 * the mark is a map square instead of an avatar, the rail holds contacts
 * instead of properties, the quick actions lead with Navigate — and none of
 * the header, tabs, Activity, Files or bottom bar is written twice.
 *
 * Two things are a property's own and nothing else's, and both are **rail
 * cards**. **Measurements** are facts about the site, so they live with the
 * site rather than on a quote. **Site & access** is the part with consequences
 * outside this screen: §6.2 says crews see access notes on the visit, so what
 * is typed there reaches somebody standing at a locked gate, which is why it
 * is a form with a Save and not a comment box.
 *
 * Site & access was a tab of its own, which is a whole screen for two text
 * boxes — and the tab row is about to carry more of them (Aaron), so the one
 * tab that is a short form rather than a list goes in the rail, under
 * Measurements, where the About card was already printing the same two values
 * read-only. Those two About rows are gone with it: one answer per question,
 * and the one that can be edited is the one to keep.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, type NavigateFunction } from 'react-router-dom'
import type { MeResponse, NotificationGrid } from '@rowted/shared'
import { RecordPage, type RecordConfig } from '../record/record-page.js'
import {
  renderStandardTab, standardTabs, type RelatedTabSpec,
} from '../record/record-standard.js'
import {
  AboutCard, NotificationDots, NotificationLegend, RecordCard, RelatedCard, RelatedRow,
  type AboutField,
} from '../record/record-rail.js'
import {
  isQuietSummary, quietMenuItems, setQuietHidden,
  type QuietConfig, type QuietStep,
} from '../record/record-quiet.js'
import { completeActivity, useRecordSummary, type RecordSummary } from '../lib/use-record.js'
import { PropertyMeasurements } from '../components/property-measurements.js'
import { PropertyMapTab } from '../property-map/property-map-tab.js'
import { LinkEditor, type LinkPatch } from '../components/link-editor.js'
import { LinkRecord } from '../components/link-record.js'
import { PropertyForm } from '../components/forms/property-form.js'
import { PropertyMap } from '../components/landing-marks.js'
import { Avatar, Button, EmptyState } from '../components/primitives.js'
import type { MenuItem } from '../components/context-menu.js'
import type { ProfileJob, ProfileVisit } from '../lib/record-payloads.js'
import {
  IconCall, IconCompany, IconEdit, IconMaps, IconNavigate, IconNote, IconPin, IconPlus,
  IconRuler, IconUpload,
} from '../frame/icons.js'
import { EM_DASH, formatDocumentDate } from '../lib/format.js'
import { request } from '../lib/api.js'
import { mapsLink } from '../lib/maps.js'
import { PlusGlyph } from '../components/keycap.js'
import { openNewRequest } from '../requests/open-request.js'

interface PropertyContact {
  id: string
  contactId: string
  role: string
  isPrimary: boolean
  isBillTo: boolean
  approvesQuotes: boolean
  notificationPrefs: NotificationGrid | null
  effectivePrefs: NotificationGrid
  /** What the editor’s "Back to the contact defaults" resets to. */
  defaultPrefs: NotificationGrid
  usingDefaults: boolean
  accessNote: string | null
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  hasPortalAccess: boolean
}

interface Property {
  id: string
  name: string
  addressLine1: string
  addressLine2: string | null
  city: string
  state: string
  postalCode: string
  accountName: string | null
  orgUnitName: string
  zoneName: string | null
  zoneColour: string | null
  propertyType: string | null
  zoneId: string | null
  accessNotes: string | null
  stagingNotes: string | null
  latitude: number | null
  longitude: number | null
  customerSince: string | null
  version: number
}

interface PropertyPayload {
  property: Property
  contacts: PropertyContact[]
  jobs: ProfileJob[]
  visits: ProfileVisit[]
}

export interface PropertyRecordData extends PropertyPayload {
  canEdit: boolean
  /** Whether the company bought Property maps — `/me`, not this record. */
  hasMaps: boolean
  /** How many live maps this property has, for the phone's green action. */
  mapCount: number
  orgUnitId: string
  onEdit: () => void
  onLink: () => void
  onReload: () => void
  onUpload: () => void
  /** The quiet panel's sheet if it is up, the Activity tab if it is not. */
  onLogActivity: () => void
  /**
   * Whether this person has hidden the Set up card on this record.
   *
   * On the record rather than read from the summary where it is needed,
   * because `RecordConfig.menu` is given the record and nothing else — and
   * the ⋯ menu is the only way back once the card is gone (§4, rule 10).
   */
  quietHidden: boolean
  onShowSetup: () => void
  onSaveLink: (linkId: string, patch: LinkPatch) => Promise<void>
  onRemoveLink: (linkId: string) => Promise<void>
}

export function PropertyRecord({ me }: { me: MeResponse }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<PropertyPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [people, setPeople] = useState<{ id: string; name: string }[]>([])
  const [editing, setEditing] = useState(false)
  const [linking, setLinking] = useState(false)
  const [completing, setCompleting] = useState<string | null>(null)
  const openUpload = useRef<(() => void) | null>(null)
  /*
   * The quiet panel's log-activity sheet, lent upwards (§4).
   *
   * The phone's green **Log activity** button lives in this page's config and
   * the sheet lives in the panel, so the panel hands its opener over the same
   * way the Files tab hands over its picker. While the record is not quiet
   * there is no panel and no sheet, and the button navigates to the Activity
   * tab as it always did.
   */
  const openLog = useRef<(() => void) | null>(null)

  const { summary, reload: reloadSummary } = useRecordSummary('property', id || undefined)

  const load = useCallback(() => {
    request<PropertyPayload>(`/properties/${id}`)
      .then((payload) => { setData(payload); setError(null) })
      .catch((cause: unknown) => setError(
        cause instanceof Error ? cause.message : 'That property could not be loaded.'))
  }, [id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    void request<{ items: { id: string; name: string }[] }>('/people?limit=200')
      .then((body) => setPeople(body.items))
      .catch(() => setPeople([]))
  }, [])

  const canEdit = me.permissions.properties === 'edit' || me.permissions.properties === 'full'
  /* Whether the Create a job step exists at all — §7, "absent, not greyed". */
  const canCreateJobs = me.permissions.jobs === 'edit' || me.permissions.jobs === 'full'
  const orgUnitId = me.scope.selected[0] ?? ''
  /*
   * Whether the company bought Property maps. It decides whether the tab is
   * *offered* — with the feature off and no maps there is nothing to show
   * (`property-maps-v2.md` §5.1 state 5) — and nothing else: a map that
   * already exists stays readable either way.
   */
  const hasMaps = me.features.includes('property_maps')
  const mapCount = summary?.counts.maps ?? 0

  const complete = useCallback(async (activityId: string) => {
    setCompleting(activityId)
    try {
      /* One occurrence of a repeating meeting by itself (`completeKey`, 0152). */
      await completeActivity(activityId)
      await reloadSummary()
    } finally {
      setCompleting(null)
    }
  }, [reloadSummary])

  const saveLink = useCallback(async (linkId: string, patch: LinkPatch) => {
    await request(`/contacts/links/${linkId}`, { method: 'PATCH', body: patch })
    load()
  }, [load])

  const removeLink = useCallback(async (linkId: string) => {
    await request(`/contacts/links/${linkId}`, { method: 'DELETE' })
    load()
    void reloadSummary()
  }, [load, reloadSummary])

  const record = useMemo<PropertyRecordData | null>(() => (data ? {
    ...data,
    canEdit,
    hasMaps,
    mapCount,
    orgUnitId,
    onEdit: () => setEditing(true),
    onLink: () => setLinking(true),
    onReload: () => { load(); void reloadSummary() },
    onUpload: () => openUpload.current?.(),
    /*
     * Read at the moment of the click, not at the moment the config was
     * built: the panel registers its opener on mount and clears it on the way
     * out, so this ref is empty exactly when there is no sheet to open.
     */
    onLogActivity: () => {
      if (openLog.current) openLog.current()
      else navigate(`/properties/${id}?tab=activity`)
    },
    quietHidden: summary?.quiet?.hidden ?? false,
    onShowSetup: () => {
      void setQuietHidden('property', id, false).then(() => reloadSummary())
    },
    onSaveLink: saveLink,
    onRemoveLink: removeLink,
  } : null), [
    data, canEdit, hasMaps, mapCount, orgUnitId, id, navigate, load, reloadSummary,
    saveLink, removeLink, summary?.quiet?.hidden,
  ])

  const config = useMemo(
    () => propertyConfig(navigate, canCreateJobs), [navigate, canCreateJobs])

  if (error) {
    return (
      <EmptyState
        title="Not found"
        body={error}
        action={{ label: 'Back to properties', onClick: () => navigate('/properties') }}
      />
    )
  }
  if (!record) return <p className="p-4 text-cell text-text-muted">Loading…</p>

  const { property, contacts } = record

  // Who is told what about this property is stored on the link rows.
  const related = { tables: ['contact_property_links'], recordIds: contacts.map((l) => l.id) }

  return (
    <>
      <RecordPage config={config} record={record} summary={summary}>
        {(tab) => (tab === 'map'
          ? (
            <PropertyMapTab
              propertyId={property.id}
              propertyName={property.name}
              address={[property.addressLine1, property.city, property.state]
                .filter(Boolean).join(', ')}
              propertyVersion={property.version}
              canEdit={canEdit}
              onOpenMeasurements={() => navigate(`/properties/${property.id}`)}
              onPropertyChanged={record.onReload}
            />
          )
          : renderStandardTab(tab, {
            object: 'property',
            id: property.id,
            href: `/properties/${property.id}`,
            summary,
            related: relatedTabs(record, navigate),
            people,
            orgUnitId,
            history: { table: 'properties', related },
            busyId: completing,
            canEdit,
            onComplete: (activityId) => void complete(activityId),
            onChanged: () => void reloadSummary(),
            onUploadReady: (open) => { openUpload.current = open },
            onLogReady: (open) => { openLog.current = open },
            quiet: config.quiet?.build(record, summary) ?? null,
            onOpen: (path) => navigate(path),
          }))}
      </RecordPage>

      <PropertyForm
        open={editing}
        onClose={() => setEditing(false)}
        existing={property}
        onSaved={() => { setEditing(false); record.onReload() }}
      />

      <LinkRecord
        open={linking}
        onClose={() => setLinking(false)}
        side="property"
        anchorId={property.id}
        excludeIds={contacts.map((link) => link.contactId)}
        onLinked={record.onReload}
      />
    </>
  )
}

/**
 * The three lists a property has, said once.
 *
 * `standardTabs` reads it to decide which tabs exist; `renderStandardTab`
 * reads the same array to draw them. That is the point of passing it around
 * rather than writing the tabs out twice — the row and the body cannot
 * disagree about which lists a property has.
 */
function relatedTabs(
  record: PropertyRecordData, navigate: NavigateFunction,
): RelatedTabSpec[] {
  const on = [{ field: 'propertyId', op: 'eq' as const, value: record.property.id }]
  return [
    {
      area: 'jobs',
      listKey: 'jobs@property',
      baseFilter: on,
      emptyBody: 'A job created against this property shows up here.',
      ...(record.canEdit
        ? {
          onNew: {
            label: 'New job',
            onClick: () => navigate(`/jobs/new?property=${record.property.id}`),
          },
        }
        : {}),
    },
    {
      area: 'visits',
      listKey: 'visits@property',
      baseFilter: on,
      emptyBody: 'Scheduling a job\u2019s work here puts its visits in this list.',
      /*
       * Schedule work, not New visit. A visit is made by scheduling a job's
       * work, never by hand (addendum §E.7), so a button offering one would
       * be a button that has to explain itself.
       */
      ...(record.canEdit
        ? { onNew: { label: 'Schedule work', onClick: () => navigate('/schedule') } }
        : {}),
    },
    {
      area: 'invoices',
      listKey: 'invoices@property',
      baseFilter: on,
      emptyBody: 'An invoice covering work here shows up here.',
      ...(record.canEdit
        ? { onNew: { label: 'New invoice', onClick: () => navigate('/invoices/new') } }
        : {}),
    },
  ]
}

// ---------------------------------------------------------------------------
// The quiet page
// ---------------------------------------------------------------------------

/**
 * What a property with nothing on it says — `quiet-records.md` §7.
 *
 * Six steps in priority order, and the order *is* the design: the lead is
 * whichever of them is still to do, so a property that has a contact but no
 * job still leads with the job (§1 rule 3). Two of them are not records at
 * all — access notes and the blank About fields are text on cards already on
 * the page — so they point at the card with `?focus=` rather than opening a
 * form (`use-focus-card.tsx`).
 *
 * **Absent, never greyed** (§1 rule 4): without `jobs:create` there is no
 * Create a job tile at all and Add a contact leads; with Property maps off
 * there is no map tile. The count is out of what is left, which is why
 * `quietProgress` does the counting rather than this.
 *
 * `build` is also what the Set up card reads on a *busy* record, so each
 * step's `state` is computed properly rather than assuming the page is
 * empty: `files` is never done while the panel is up (a quiet record has no
 * files, by definition) and is perfectly able to be done under the card.
 */
export function propertyQuiet(
  record: PropertyRecordData,
  canCreateJobs: boolean,
  navigate: NavigateFunction,
  /** What is connected to it — the file count is the one thing only this says. */
  summary: RecordSummary | null,
): QuietConfig {
  const { property, contacts, canEdit } = record
  const files = summary?.counts.files ?? 0
  const here = `/properties/${property.id}`
  const street = property.addressLine1

  /* Only the blanks are named, so the line says what is actually missing. */
  const blanks = [
    ...(property.zoneId ? [] : ['Zone']),
    ...(property.accountName ? [] : ['account']),
  ]

  const steps: QuietStep[] = [
    {
      key: 'job',
      icon: 'jobs',
      title: 'Create a job',
      body: `Quote or schedule work at ${street}.`,
      /*
       * Done once there is a job. A property with a job is not quiet any
       * more, so this is only ever read by the Set up card — and board 5's
       * busy cell (one job on Santos Home, *Set up Santos Home · 5 of 6*,
       * only Add access notes left) draws it done there. Offering "Create a
       * job" under a Jobs card that already has one was the walkthrough's
       * defect (Oct 30, §8).
       */
      state: !canCreateJobs ? 'absent' : (summary?.counts.jobs ?? 0) > 0 ? 'done' : 'todo',
      onSelect: () => navigate(`/jobs/new?property=${property.id}`),
    },
    {
      key: 'contact',
      icon: 'contacts',
      title: 'Add a contact',
      body: 'Who pays, who approves, who to call.',
      state: !canEdit ? 'absent' : contacts.length > 0 ? 'done' : 'todo',
      doneBody: contacts.length === 1
        ? `${contacts[0]!.firstName} ${contacts[0]!.lastName}`.trim()
        : `${contacts.length} contacts`,
      onSelect: record.onLink,
    },
    {
      key: 'map',
      icon: 'map',
      title: 'Create a map',
      body: 'Zones, gates and hazards, shared with crews.',
      /* The feature is Rowted's to sell, so with it off the step is absent
         rather than an invitation to a tab that is not there (§5.1 state 5). */
      state: !canEdit || !record.hasMaps ? 'absent' : record.mapCount > 0 ? 'done' : 'todo',
      doneBody: record.mapCount === 1 ? '1 map' : `${record.mapCount} maps`,
      onSelect: () => navigate(`${here}?tab=map&new=1`),
    },
    {
      key: 'access',
      icon: 'access',
      title: 'Add access notes',
      body: 'Gate codes, parking and pets — read on the visit.',
      state: !canEdit ? 'absent' : (property.accessNotes ?? '').trim() ? 'done' : 'todo',
      doneBody: 'Crews have what they need at the gate.',
      onSelect: () => navigate(`${here}?focus=site`),
    },
    {
      key: 'details',
      icon: 'details',
      title: 'Finish the details',
      /* "Zone and account are blank." — the mockup's own sentence, built from
         what is actually blank rather than from the whole list. */
      body: blanks.length === 0
        ? 'Everything About asks for is filled in.'
        : `${upperFirst(sentenceList(blanks))} ${blanks.length === 1 ? 'is' : 'are'} blank.`,
      state: !canEdit ? 'absent' : blanks.length === 0 ? 'done' : 'todo',
      doneBody: 'Type, zone, branch and account are all set.',
      onSelect: () => navigate(`${here}?focus=about`),
    },
    {
      key: 'files',
      icon: 'files',
      kind: 'files',
      title: 'Attach files',
      body: 'Drop photos, contracts or plans anywhere on this page.',
      /* A phone has no drop and no file manager worth opening blind, so the
         words change with the gesture — the one place they are allowed to. */
      phone: {
        title: 'Add photos or files',
        body: 'Take a photo or pick from your library.',
      },
      /* Done once there are any. It can never be done while the *panel* is
         up — a record with a file is not quiet — but the Set up card lives
         on a record that has started filling up, and a step still asking for
         what is already there is what keeps that card alive for ever. */
      state: !canEdit ? 'absent' : files > 0 ? 'done' : 'todo',
      doneBody: files === 1 ? '1 file' : `${files} files`,
    },
  ]

  return {
    headline: `Nothing is connected to ${property.name} yet`,
    sub: 'Write down what you know, or pick a first step. Each one shows up on this page as'
      + ' soon as it\u2019s done.',
    composerSubject: property.name,
    setupSubject: property.name,
    steps,
    ghosts: [
      {
        title: 'Next up',
        line: 'Tasks, calls and visits waiting on this property.',
        icon: 'clock',
      },
      { title: 'Jobs', line: `Quotes and work at ${street}.`, icon: 'jobs' },
      { title: 'Notes', line: 'Pinned notes and what the crew should know.', icon: 'note' },
    ],
    canWrite: canEdit,
  }
}

/** "Zone", "Zone and account", "Zone, account and branch". */
function sentenceList(words: string[]): string {
  if (words.length <= 1) return words[0] ?? ''
  return `${words.slice(0, -1).join(', ')} and ${words.at(-1)}`
}

/**
 * The sentence starts with a capital, whichever field happens to be first.
 *
 * The blanks are named in About's own order and in About's own words, so
 * "Zone and account are blank" reads right and — with only the account blank
 * — "account is blank." does not. Capitalising the sentence rather than each
 * field is what keeps both: the list is still a list of field names.
 */
const upperFirst = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)

// ---------------------------------------------------------------------------
// The configuration
// ---------------------------------------------------------------------------

function propertyConfig(
  navigate: NavigateFunction,
  /**
   * Whether this viewer may create a job.
   *
   * It decides which step *leads* (§7: `job`, falling back to `contact`),
   * and it is a permission rather than a fact about the record — so it comes
   * from `/me` at the page, not from the payload.
   */
  canCreateJobs: boolean,
): RecordConfig<PropertyRecordData> {
  const addressOf = (property: Property) => [
    property.addressLine1, property.addressLine2, property.city,
  ].filter(Boolean).join(', ')

  const mapsOf = (property: Property) => mapsLink([
    property.addressLine1, property.addressLine2,
    property.city, property.state, property.postalCode,
  ])

  return {
    object: 'property',

    breadcrumb: (record) => [
      { label: 'Properties', href: '/properties' },
      { label: record.property.name },
    ],

    /*
     * The same map square the list draws, at hero size, and it opens Google
     * Maps. What somebody opening a property wants from that corner is "where
     * is this", and one click to the map is the honest answer.
     */
    mark: (record) => {
      const maps = mapsOf(record.property)
      const square = (
        <PropertyMap
          seed={record.property.id}
          colour={record.property.zoneColour}
          size={56}
          radius={12}
          title={record.property.zoneName ?? 'No zone'}
        />
      )
      return maps
        ? (
          <a
            {...maps}
            title={`Open ${addressOf(record.property)} in Google Maps`}
            className="block rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
          >
            {square}
          </a>
        )
        : square
    },

    title: (record) => record.property.name,

    tags: (record) => [
      ...(record.property.propertyType ? [{ label: record.property.propertyType }] : []),
      ...(record.property.zoneName ? [{ label: record.property.zoneName }] : []),
    ],

    meta: (record) => {
      const { property } = record
      const maps = mapsOf(property)
      const full = `${addressOf(property)}, ${property.state} ${property.postalCode}`
      return [
        {
          icon: <IconPin size={13} />,
          text: full,
          ...(maps ? { href: maps.href } : {}),
        },
        { icon: <IconCompany size={13} />, text: property.orgUnitName },
        ...(property.accountName ? [{ text: `Managed by ${property.accountName}` }] : []),
        ...(property.customerSince
          ? [{ text: `Customer since ${new Date(property.customerSince).getFullYear()}` }]
          : []),
      ]
    },

    primary: (record) => (record.canEdit
      ? {
        label: 'New job',
        icon: <IconPlus size={14} />,
        onClick: () => navigate(`/jobs/new?property=${record.property.id}`),
      }
      : null),

    /*
     * Edit is first and it is a button, not a menu item. It is the thing
     * somebody came to do on a record more often than anything else here, and
     * it was two clicks behind a ⋯ that gave no clue it was in there.
     */
    actions: (record) => {
      const maps = mapsOf(record.property)
      return [
        ...(record.canEdit
          ? [{ label: 'Edit', icon: <IconEdit size={14} />, onClick: record.onEdit }]
          : []),
        ...(maps
          ? [{
            label: 'Navigate',
            icon: <IconNavigate size={14} />,
            onClick: () => window.open(maps.href, '_blank', 'noopener'),
          }]
          : []),
      ]
    },

    menu: (record) => {
      const items: MenuItem[] = []
      /* First, for whoever may take one (`new-request.md` §13.3, Board 1 H). */
      if (canCreateJobs) {
        items.push({
          key: 'new-request',
          label: `New request at ${record.property.name}`,
          sub: `${record.property.name} filled in, its contacts offered`,
          icon: <PlusGlyph size={15} stroke={2.2} />,
          keys: ['N', 'R'],
          onSelect: () => openNewRequest({ propertyId: record.property.id }),
        })
      }
      if (record.canEdit) items.push({ key: 'link', label: 'Add a contact', onSelect: record.onLink })
      /* The one way back to a hidden Set up card — `quietMenuItems` so every
         record calls it the same thing. */
      items.push(...quietMenuItems(record.quietHidden, record.onShowSetup))
      return items
    },

    /*
     * Site & access is a rail card (see the note at the top), so the only
     * `own` tab is **Map**.
     *
     * It is offered when the company has the feature *or* there are maps
     * already — §5.1 state 5: turning Property maps off must not hide a plan
     * the crews are working from, and with it off and nothing drawn there is
     * no tab. The count is dropped at zero, because the Empty screen is the
     * tab's own first page rather than an absence.
     */
    tabs: (record, summary) => {
      const maps = summary?.counts.maps ?? 0
      return standardTabs({
        counts: summary?.counts ?? {},
        related: relatedTabs(record, navigate),
        own: record.hasMaps || maps > 0
          ? [{
            key: 'map',
            label: 'Map',
            icon: 'map' as const,
            ...(maps > 0 ? { count: maps } : {}),
            /* The editor is a canvas and a 300px panel; it does not fit
               beside a 340px rail (§5.3, the `wide` rule). */
            wide: true,
          }]
          : [],
      })
    },

    rail: (record) => <PropertyRail record={record} />,

    /*
     * The shell reads `when` for one thing only: on a phone the panel comes
     * before About and the busy Overview comes after it. The predicate is the
     * Overview's own, so the two cannot disagree about which this is.
     */
    quiet: {
      when: (record, summary) => isQuietSummary(summary, relatedTabs(record, navigate)),
      build: (record, summary) => propertyQuiet(record, canCreateJobs, navigate, summary),
    },

    phone: {
      /*
       * Navigate first. Somebody opening a property on a phone is, more often
       * than not, in a truck deciding where to turn — and the second and third
       * are the person to call when they arrive and cannot get in.
       */
      quickActions: (record, tab) => {
        const maps = mapsOf(record.property)
        const primary = record.contacts.find((link) => link.isPrimary) ?? record.contacts[0]
        return [
          ...(maps
            ? [{ label: 'Navigate', icon: <IconNavigate size={18} />, href: maps.href }]
            : []),
          ...(primary?.phone
            ? [{ label: 'Call', icon: <IconCall size={18} />, href: `tel:${primary.phone}` }]
            : []),
          /*
           * Measure is a quick action, and only on the Map tab
           * (`property-maps-v2.md` §6.1). It is the second thing somebody does
           * on that tab and the only way to it on a phone, where there is no
           * tool rail to put it in; on every other tab the slot is better
           * spent on the two above.
           */
          ...(tab === 'map' && record.canEdit && record.hasMaps
            ? [{
              label: 'Measure',
              icon: <IconRuler size={18} />,
              onClick: () => navigate(
                `/properties/${record.property.id}?tab=map&measure=1`),
            }]
            : []),
        ]
      },
      /*
       * Four cells and Map is not one of them: §6.1 puts it under **More**
       * with its count, because a property's bottom bar is for the four things
       * somebody reaches for standing in a yard.
       */
      tabBar: ['overview', 'activity', 'jobs', 'visits'],
      primaryByTab: (tab, record) => {
        if (tab === 'map') {
          /*
           * §6.1: the green action follows the tab — `New map` with no maps
           * and `Open map` with maps. It is drawn even without edit rights
           * when there is something to open, because looking at a map is not
           * editing one; with nothing to open and no rights there is nothing
           * to offer.
           */
          const hasMaps = (record.mapCount ?? 0) > 0
          if (hasMaps) {
            return {
              label: 'Open map',
              icon: <IconMaps size={18} />,
              onClick: () => navigate(`/properties/${record.property.id}?tab=map&open=1`),
            }
          }
          return record.canEdit && record.hasMaps
            ? {
              label: 'New map',
              icon: <IconMaps size={18} />,
              onClick: () => navigate(`/properties/${record.property.id}?tab=map&new=1`),
            }
            : null
        }
        if (!record.canEdit || tab === 'activity') return null
        if (tab === 'files') {
          return { label: 'Upload', icon: <IconUpload size={18} />, onClick: record.onUpload }
        }
        if (tab === 'overview') {
          return {
            label: 'Log activity',
            icon: <IconNote size={18} />,
            /*
             * The panel's own sheet while it is up, and the Activity tab
             * otherwise — the record decides, because only it can see whether
             * the panel is mounted (§4). Navigating away from a quiet
             * Overview to write the first note is the long way round to the
             * box already on the screen, which on a phone is a trigger for
             * that very sheet.
             */
            onClick: record.onLogActivity,
          }
        }
        return {
          label: 'New job',
          icon: <IconPin size={18} />,
          onClick: () => navigate(`/jobs/new?property=${record.property.id}`),
        }
      },
    },
  }
}

// ---------------------------------------------------------------------------
// A property's own pieces
// ---------------------------------------------------------------------------

function PropertyRail({ record }: { record: PropertyRecordData }) {
  const navigate = useNavigate()
  const { property, contacts } = record
  const [editingLink, setEditingLink] = useState<PropertyContact | null>(null)

  const fields: AboutField[] = [
    { label: 'Type', value: property.propertyType ?? EM_DASH },
    { label: 'Zone', value: property.zoneName ?? EM_DASH },
    { label: 'Branch', value: property.orgUnitName },
    { label: 'Account', value: property.accountName ?? EM_DASH },
    {
      label: 'Customer since',
      value: property.customerSince ? formatDocumentDate(property.customerSince) : EM_DASH,
    },
    /* Access and Staging are not here: the Site & access card below
       Measurements is the one place they are shown, and there they can be
       changed. */
  ]

  return (
    <>
      {/* `?focus=about` is where "Finish the details" lands (§7). */}
      <AboutCard
        fields={fields}
        onEdit={record.canEdit ? record.onEdit : undefined}
        focusKey="about"
        {...(record.canEdit ? { onFocus: record.onEdit } : {})}
      />

      <RelatedCard
        title="Contacts"
        count={contacts.length}
        addLabel={record.canEdit ? 'Add' : undefined}
        onAdd={record.canEdit ? record.onLink : undefined}
        legend={contacts.length > 0 ? <NotificationLegend /> : undefined}
      >
        {contacts.length === 0
          ? (
            <p className="m-0 px-[18px] py-3 text-body text-text-muted">
              Nobody is on this property yet.
            </p>
          )
          : contacts.map((link) => {
            const name = `${link.firstName} ${link.lastName}`.trim()
            return (
              <RelatedRow
                key={link.id}
                mark={<Avatar name={name} size={34} />}
                title={name}
                sub={(
                  <>
                    {link.role}
                    {link.isPrimary && ' · primary'}
                    {link.isBillTo && ' · bill to'}
                    {link.approvesQuotes && ' · approves quotes'}
                  </>
                )}
                dots={(
                  <NotificationDots
                    grid={link.effectivePrefs as unknown as
                      Record<string, Record<string, boolean | undefined>>}
                    onOpen={() => setEditingLink(link)}
                  />
                )}
                /*
                  * The row opens the **contact**, and the dots open the link.
                  *
                  * It opened the link from anywhere, on the argument that a
                  * click here is almost always about the relationship. In use
                  * that is the wrong way round: a row carrying somebody’s face
                  * and name reads as a link to that person, and an edit dialog
                  * instead is a click nobody asked for. The relationship keeps
                  * its own target — the oval of dots, the one part of the row
                  * that is about the link rather than the record.
                  */
                onOpen={() => navigate(`/contacts/${link.contactId}`)}
              />
            )
          })}
      </RelatedCard>

      {/* It draws its own card, so its header can carry Add like the others. */}
      <PropertyMeasurements propertyId={property.id} canEdit={record.canEdit} />

      <SiteAndAccess
        property={property}
        canEdit={record.canEdit}
        onSaved={record.onReload}
      />

      {editingLink && (
        <LinkEditor
          link={editingLink}
          title={`${editingLink.firstName} ${editingLink.lastName}`.trim()}
          subtitle={[editingLink.phone, editingLink.email].filter(Boolean).join(' · ')
            || editingLink.role}
          canEdit={record.canEdit}
          onClose={() => setEditingLink(null)}
          onSave={(patch) => record.onSaveLink(editingLink.id, patch)}
          onRemove={record.canEdit
            ? () => record.onRemoveLink(editingLink.id)
            : undefined}
          onOpenRecord={() => navigate(`/contacts/${editingLink.contactId}`)}
          openRecordLabel="Open contact"
        />
      )}
    </>
  )
}

/**
 * Site & access — a rail card under Measurements.
 *
 * §6.2: "crews see access notes on the visit". This is the one field in the
 * product whose reader is standing at a gate on a phone, which is why it is a
 * form with a Save rather than a note in the timeline: a note can be argued
 * with and superseded, and the crew needs the current answer.
 *
 * It was a tab, which is a whole screen for two text boxes, and the tab row
 * has other things to carry (Aaron). The rail is 360px rather than the width
 * of the page, so the helper lines are one short sentence each and the boxes
 * are three rows and two rather than four and three — enough to see what is
 * written without the card running past the fold. The note belongs *under*
 * the label and above the box, which is where `RecordCard`'s own panes put
 * theirs, and the footer rule is the measurements card's.
 */
function SiteAndAccess({
  property, canEdit, onSaved,
}: {
  property: Property
  canEdit: boolean
  onSaved: () => void
}) {
  const [access, setAccess] = useState<string | null>(null)
  const [staging, setStaging] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const dirty = access !== null || staging !== null
  /*
   * Where "Add access notes" lands (§7). The card's *focus* is the caret in
   * the Access notes box rather than merely the card on screen: the step is
   * "add access notes", and a scroll that leaves somebody still having to
   * find and click the right one of two text boxes has done half the job.
   */
  const accessBox = useRef<HTMLTextAreaElement>(null)

  async function save() {
    setSaving(true)
    try {
      await request(`/properties/${property.id}`, {
        method: 'PATCH',
        body: {
          accessNotes: access ?? property.accessNotes ?? '',
          stagingNotes: staging ?? property.stagingNotes ?? '',
          version: property.version,
        },
      })
      setAccess(null)
      setStaging(null)
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  const box = 'w-full resize-y rounded-field border border-line bg-field p-2'
    + ' text-cell text-text placeholder:text-text-faint'

  return (
    <RecordCard
      title="Site & access"
      footer="Crews read this on the visit, on a phone, at the gate."
      focusKey="site"
      onFocus={() => accessBox.current?.focus()}
    >
      <div className="flex flex-col gap-3 px-[18px] pb-4 pt-3.5">
        <label className="block">
          <span className="mb-1 block text-sub text-text-muted">Access notes</span>
          <textarea
            ref={accessBox}
            value={access ?? property.accessNotes ?? ''}
            onChange={(event) => setAccess(event.target.value)}
            readOnly={!canEdit}
            rows={3}
            placeholder="North gate 42 in — mini-skid only. Call 20 min ahead."
            className={box}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sub text-text-muted">Staging</span>
          <textarea
            value={staging ?? property.stagingNotes ?? ''}
            onChange={(event) => setStaging(event.target.value)}
            readOnly={!canEdit}
            rows={2}
            placeholder="Park on the service road. Do not block the dock before 9."
            className={box}
          />
        </label>

        {canEdit && dirty && (
          <div className="flex gap-2">
            <Button variant="primary" disabled={saving} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            <Button onClick={() => { setAccess(null); setStaging(null) }}>Cancel</Button>
          </div>
        )}
      </div>
    </RecordCard>
  )
}
